import { useCallback } from "react";
import { useSWRConfig } from "swr";
import { unstable_serialize } from "swr/infinite";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Thread } from "@/models/agent/thread";
import { createThreadsInfiniteKeyLoader } from "./useInfiniteThreads";

export const useThreadMutations = () => {
  const { mutate } = useSWRConfig();
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  const pushOptimisticThread = useCallback((threadId: string, content: string, metadata?: Record<string, string>) => {
    if (!sessionCurrentWorkspaceId) return;

    const key = unstable_serialize(createThreadsInfiniteKeyLoader(sessionCurrentWorkspaceId));

    const newThread = new Thread(threadId, sessionCurrentWorkspaceId, content, new Date(), new Date(), metadata);

    mutate(
      key,
      (currentPages?: Thread[][]) => {
        if (!currentPages || currentPages.length === 0) return [[newThread]];
        const [firstPage, ...rest] = currentPages;
        return [[newThread, ...firstPage], ...rest];
      },
      { revalidate: false },
    );
  }, [sessionCurrentWorkspaceId, mutate]);

  const replaceThread = useCallback((thread: Thread) => {
    if (!sessionCurrentWorkspaceId) return;

    const key = unstable_serialize(createThreadsInfiniteKeyLoader(sessionCurrentWorkspaceId));

    mutate(
      key,
      (currentPages?: Thread[][]) => {
        if (!currentPages) return currentPages;
        return currentPages.map((page) => page.map((t) => (t.id === thread.id ? thread : t)));
      },
      { revalidate: false },
    );
  }, [sessionCurrentWorkspaceId, mutate]);

  return { pushOptimisticThread, replaceThread };
};
